/* GoBuildIt — gbi-cart.js (owner E1). Spec: docs/specs/gbi-cart-shop-rewards-spec.md §4.4 and §5.4.
   Loaded once per page (defer) by snippets/cart-drawer.liquid, OUTSIDE #CartDrawer, so it survives every
   drawer re-render. Three jobs:
   1. <gbi-cart-shop>: the empty cart as a mini collection page. The element lives inside .drawer__inner-empty
      and is re-created by every re-render; all state (shelf HTML cache, last tab, scroll) is module-level.
      Nothing is fetched until the drawer is open (or the cart icon is hovered/touched: prefetch).
   2. Kit recommendations under the items of a filled cart (2026-10-14): the same <gbi-cart-shop>, printed by
      snippets/gbi-cart-empty.liquid in mode 'rec' (data-gbi-rec), always open. Kits already in the cart are
      left out. (<gbi-cart-more>, the older "Add another kit" toggle, is kept for markup cached before that.)
   3. The reward line celebration: .is-new once per session, .is-bump when the kit count goes up.
   Vanilla ES2017, no dependency except window.GBI (gbi-core.js: GBI.cart.add, GBI.track). */
(function () {
  'use strict';
  if (window.customElements && customElements.get('gbi-cart-shop')) return;

  var SECTION = 'gbi-cart-shelf';
  var cache = new Map();                 // shelf url -> html (finished responses)
  var inflight = new Map();              // shelf url -> promise (one request per url, shared by prefetch and tabs)
  var state = { key: null, sub: null, scroll: {}, more: false };
  var recState = { key: null, sub: null };   // the filled cart's recommendations remember their own tab (2026-10-18)
  var adding = 0;                        // time the running quick add started (one at a time, spec §4.4.6)
  var shopTpl = null;                    // pristine empty-shop markup, reused by "Add another kit" (§4.7)
  var TPL_KEY = 'gbi:cshop-tpl';
  var doc = document;

  function track(event, params) {
    try { if (window.GBI && typeof GBI.track === 'function') GBI.track(event, params || {}); } catch (e) { /* ignore */ }
  }
  function shelfUrl(url) { return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + SECTION; }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function reduced() { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); }
  function ss(k, v) {
    try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; }
    return null;
  }

  // One request per shelf URL: a prefetch (touchstart / hover on the cart icon) and the tab that opens ~100 ms
  // later share the same promise. Nothing is aborted (a shelf is ≈ 2.5 KB gzip and a finished answer fills the
  // cache for later); a stale answer is only not rendered (token check in show()). One retry on a network
  // error, 429 or 5xx, after Retry-After (capped at 4 s) or 1.2 s (Shopify rate-limits fresh sessions).
  function load(url) {
    var u = shelfUrl(url);
    if (cache.has(u)) return Promise.resolve(cache.get(u));
    if (inflight.has(u)) return inflight.get(u);
    function run(attempt) {
      return fetch(u, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(function (res) {
        if (!res.ok) {
          var err = new Error('shelf ' + res.status);
          err.status = res.status;
          err.retryAfter = Number(res.headers.get('Retry-After'));
          throw err;
        }
        return res.text();
      }).catch(function (err) {
        if (attempt > 0 || (err.status && err.status < 500 && err.status !== 429)) throw err;
        var wait = Math.min(err.retryAfter > 0 ? err.retryAfter * 1000 : 1200, 4000);
        return sleep(wait).then(function () { return run(1); });
      });
    }
    var p = run(0).then(function (html) {
      if (html.indexOf('data-gbi-shelf') === -1) throw new Error('shelf markup');
      cache.set(u, html);
      return html;
    });
    inflight.set(u, p);
    var done = function () { inflight.delete(u); };
    p.then(done, done);
    return p;
  }

  function currentShop() { return doc.querySelector('cart-drawer .drawer__inner-empty gbi-cart-shop'); }

  // Prefetch the tab the shop will open on, while the pointer/finger is on the way to the cart icon.
  var prefetched = false;
  function prefetch(e) {
    if (prefetched || !e.target || !e.target.closest || !e.target.closest('#cart-icon-bubble')) return;
    var drawer = doc.querySelector('cart-drawer');
    var shop = currentShop();
    if (!drawer || !drawer.classList.contains('is-empty') || !shop) return;
    var tab = (state.key && shop.querySelector('[data-key="' + state.key + '"][data-url]')) || shop.querySelector('[role="tab"]');
    if (!tab || !tab.dataset.url) return;
    prefetched = true;
    load((state.sub && shop.querySelector('.gbi-cshop__sub[data-key="' + state.sub + '"]') || tab).dataset.url).catch(function () { prefetched = false; });
  }
  doc.addEventListener('pointerover', prefetch, { passive: true });
  doc.addEventListener('touchstart', prefetch, { passive: true });
  doc.addEventListener('focusin', prefetch);

  // Esc inside the version sheet closes the sheet only: the theme drawer closes on keyup Escape. Armed only by
  // a real Escape keydown while a sheet is open (not by `cancel`, which an Android back gesture also fires
  // with no keyup after it), and disarmed after 1 s in any case.
  var swallowEscUp = false;
  var escTimer = 0;
  function isEsc(e) { return e.key === 'Escape' || e.code === 'Escape'; }
  window.addEventListener('keydown', function (e) {
    if (!isEsc(e) || !doc.querySelector('cart-drawer dialog.gbi-csheet[open]')) return;
    swallowEscUp = true;
    clearTimeout(escTimer);
    escTimer = setTimeout(function () { swallowEscUp = false; }, 1000);
  }, true);
  window.addEventListener('keyup', function (e) {
    if (swallowEscUp && isEsc(e)) { swallowEscUp = false; clearTimeout(escTimer); e.stopPropagation(); }
  }, true);

  // Enter in a drawer quantity field (2026-10-22, open question #130a). The drawer form's only active submit
  // button is Checkout, so the browser's Enter-to-submit sent shoppers straight to checkout with the old
  // quantity. Enter now never submits that form: it does what leaving the field does (a change event for the
  // theme's quantity handler) and keeps the focus in the field. Capture phase, before the theme's handlers.
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || e.isComposing) return;
    var el = e.target;
    if (!el || el.tagName !== 'INPUT' || !el.form || el.form.id !== 'CartDrawer-Form') return;
    e.preventDefault();
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  class GbiCartShop extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.drawer = this.closest('cart-drawer');
      // Where this shop lives: the empty cart, or the recommendations under the items of a filled cart.
      this.place = (this.hasAttribute('data-gbi-rec') || this.closest('gbi-cart-more')) ? 'more' : 'empty';
      if (this.place === 'empty') {
        // Keep the pristine markup (before labels, selection, thumbnails) for "Add another kit": in memory for
        // this page, in sessionStorage for the next pages of the visit (those load with a filled cart).
        shopTpl = this.outerHTML;
        ss(TPL_KEY, shopTpl);
        // Scope the empty-cart scroller layout to a drawer that really holds the shop (gbi-cart-shop.css): with
        // the shop switched off, today's empty cart keeps today's layout exactly.
        var host = this.closest('.drawer__inner-empty');
        if (host) host.classList.add('gbi-cshop-host');
      }
      var cfgEl = this.querySelector('[data-gbi-cshop-config]');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }
      this.s = this.cfg.strings || {};
      this.rail = this.querySelector('[data-gbi-cshop-rail]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.panel = this.querySelector('[role="tabpanel"]');
      this.shelf = this.querySelector('[data-gbi-cshop-shelf]');
      this.titleEl = this.querySelector('[data-gbi-cshop-title]');
      this.allEl = this.querySelector('[data-gbi-cshop-all]');
      this.statusEl = this.querySelector('[data-gbi-cshop-status]');
      this.sheet = this.querySelector('[data-gbi-csheet]');
      this.token = 0;
      if (!this.tabs.length || !this.shelf) return;
      this.fill(this);

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.addEventListener('click', this.onClick);
      this.rail.addEventListener('keydown', this.onKey);
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      this.shelf.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      if (this.sheet) {
        this.sheet.addEventListener('close', this.onSheetClose.bind(this));
        this.sheet.addEventListener('click', this.onSheetClick.bind(this));
        this.sheet.addEventListener('change', this.onSheetChange.bind(this));
      }

      this.st = this.place === 'more' ? recState : state;
      var key = this.st.key && this.querySelector('[role="tab"][data-key="' + this.st.key + '"]') ? this.st.key : this.tabs[0].dataset.key;
      this.select(key, this.st.sub, { restore: true });

      this.wasOpen = false;
      this.mo = new MutationObserver(this.syncOpen.bind(this));
      if (this.drawer) this.mo.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      // Back to empty while open (last item removed): the theme focuses the first link of the drawer, which
      // is now a shop link. Put focus on the heading instead, so "Your cart is empty" is what is announced.
      if (this.isOpen()) {
        var self = this;
        requestAnimationFrame(function () {
          var a = doc.activeElement;
          var wrap = self.closest('.drawer__inner-empty');
          var h = wrap && wrap.querySelector('.gbi-cart-head__title');
          if (h && (!a || a === doc.body || (self.contains(a) && a.tagName === 'A'))) {
            h.setAttribute('tabindex', '-1');
            try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
          }
        });
      }
      this.syncOpen();
    }

    disconnectedCallback() {
      if (this.mo) this.mo.disconnect();
      this._ready = false;
    }

    isOpen() { return !!(this.drawer && this.drawer.classList.contains('active')); }

    syncOpen() {
      var open = this.isOpen();
      if (open && !this.wasOpen) this.activate();
      this.wasOpen = open;
    }

    activate() {
      if (!this.activated) {
        this.activated = true;
        this.querySelectorAll('img[data-src]').forEach(function (img) { img.src = img.getAttribute('data-src'); img.removeAttribute('data-src'); });
        this.show();
      } else if (this.shelf.querySelector('.gbi-cshop__error')) {
        this.show();   // the last load failed: reopening the drawer retries (no need to find "Try again")
      }
      track('cart_shelf_view', { tab: this.cur ? this.cur.label : '', count: this.cur ? this.cur.count : '', place: this.place });
    }

    fill(root) {
      var s = this.s;
      root.querySelectorAll('[data-gbi-l]').forEach(function (el) {
        var v = s[el.getAttribute('data-gbi-l')];
        if (v != null && el.textContent !== v) el.textContent = v;
      });
    }

    // ---------------------------------------------------------------- selection
    select(key, subKey, opts) {
      opts = opts || {};
      var tab = this.querySelector('[role="tab"][data-key="' + key + '"]');
      if (!tab) return;
      this.tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      this.panel.setAttribute('aria-labelledby', tab.id);
      var group = null;
      this.querySelectorAll('.gbi-cshop__subs').forEach(function (g) {
        var mine = g.getAttribute('data-parent') === key;
        g.hidden = !mine;
        if (mine) group = g;
      });
      var target = tab;
      if (group) {
        var chips = Array.prototype.slice.call(group.querySelectorAll('.gbi-cshop__sub'));
        var chip = (subKey && group.querySelector('.gbi-cshop__sub[data-key="' + subKey + '"]')) || chips[0];
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        if (chip && chip.dataset.url) target = chip;
        subKey = chip && chip !== chips[0] ? chip.dataset.key : null;
      } else {
        subKey = null;
      }
      var label = (target === tab ? tab.querySelector('.gbi-cshop__label') : target).textContent.trim();
      if (target !== tab && target === (group && group.querySelector('.gbi-cshop__sub'))) label = tab.querySelector('.gbi-cshop__label').textContent.trim();
      this.cur = { key: key, sub: subKey, url: target.dataset.url, label: label, count: target.dataset.count || '', pos: Number(tab.dataset.pos) || 0, search: target.hasAttribute('data-search') };
      // Recommendations (filled cart): a tab that is not kits (e.g. "Display it") brings its own heading, so the
      // "free mystery gift with every kit" line never sits above display cases (2026-10-14).
      if (this.place === 'more') {
        var rec = this.closest('.gbi-crec');
        var ht = rec && rec.querySelector('[data-gbi-crec-title]');
        var hs = rec && rec.querySelector('[data-gbi-crec-sub]');
        var own = tab.hasAttribute('data-rec-title');
        if (ht) ht.textContent = own ? tab.getAttribute('data-rec-title') : ht.getAttribute('data-default');
        if (hs) {
          var sub = own ? (tab.getAttribute('data-rec-sub') || '') : hs.getAttribute('data-default');
          hs.textContent = sub;
          hs.hidden = !sub;
        }
      }
      this.st.key = key;
      this.st.sub = subKey;
      this.head();
      if (opts.restore) this.reveal(tab, true);
      if (this.activated) this.show();
    }

    head() {
      var c = this.cur;
      if (!c) return;
      var count = c.count !== '' && c.count != null ? String(this.s.models || '[count]').replace('[count]', Number(c.count).toLocaleString('en')) : '';
      this.titleEl.textContent = '';
      var t = doc.createElement('span');
      t.textContent = c.label;
      this.titleEl.appendChild(t);
      if (count) {
        var n = doc.createElement('span');
        n.className = 'gbi-cshop__count';
        n.textContent = ' · ' + count;
        this.titleEl.appendChild(n);
      }
      // A search tab shows every matching kit already, and the store search pads its results with unrelated
      // kits: no "View all" there (the shelf has no end card either, gbi-cart-shelf.liquid).
      if (this.allEl) { this.allEl.href = c.url; this.allEl.hidden = !!c.search; }
    }

    reveal(el, instant) {
      var r = this.rail;
      if (!r || !el) return;
      var left = el.offsetLeft - r.offsetLeft;
      var pad = 16;
      var target = r.scrollLeft;
      if (left - pad < r.scrollLeft) target = left - pad;
      else if (left + el.offsetWidth + pad > r.scrollLeft + r.clientWidth) target = left + el.offsetWidth + pad - r.clientWidth;
      if (target !== r.scrollLeft) r.scrollTo({ left: Math.max(0, target), behavior: instant || reduced() ? 'auto' : 'smooth' });
    }

    // ---------------------------------------------------------------- shelf
    show() {
      var self = this;
      var c = this.cur;
      if (!c || !c.url) return;
      var u = shelfUrl(c.url);
      var token = ++this.token;
      if (cache.has(u)) { this.render(cache.get(u)); return; }
      this.skeleton();
      load(c.url).then(function (html) {
        if (token === self.token && self.isConnected) self.render(html);
      }).catch(function () {
        if (token === self.token && self.isConnected) self.fail();
      });
    }

    skeleton() {
      var list = this.shelf.querySelector('.gbi-cshelf__list--skeleton');
      if (!list) {
        var html = '<ul class="gbi-cshelf__list gbi-cshelf__list--skeleton" role="list" aria-hidden="true">';
        for (var i = 0; i < 3; i++) html += '<li class="gbi-ccard gbi-ccard--skeleton"><span class="gbi-ccard__media"></span><span class="gbi-ccard__facts"></span><span class="gbi-ccard__body"><span></span><span></span><span></span></span><span class="gbi-ccard__add"></span></li>';
        this.shelf.innerHTML = html + '</ul>';
      }
      this.shelf.setAttribute('aria-busy', 'true');
      this.statusEl.textContent = '';
      this.arrows();
    }

    render(html) {
      var tpl = doc.createElement('template');
      tpl.innerHTML = html;
      var root = tpl.content.querySelector('[data-gbi-shelf]');
      if (!root) { this.fail(); return; }
      this.fill(root);
      var countEl = root.querySelector('[data-gbi-shelf-count]');
      if (countEl && (this.cur.count === '' || this.cur.count == null)) {
        this.cur.count = countEl.getAttribute('data-gbi-shelf-count');
        this.head();
      }
      // The recommendations never offer a kit that is already in the cart (the shelf HTML is cached per page, so
      // the filter runs on every render, against the drawer's current lines).
      if (this.place === 'more' && this.drawer) {
        var inCart = {};
        this.drawer.querySelectorAll('.cart-item').forEach(function (li) {
          var m = /(?:^|\s)cart-item--product-(\S+)/.exec(li.className);
          if (m) inCart[m[1]] = true;
        });
        root.querySelectorAll('.gbi-ccard[data-handle]').forEach(function (card) {
          if (inCart[card.getAttribute('data-handle')]) card.remove();
        });
      }
      var frag = doc.createDocumentFragment();
      Array.prototype.slice.call(root.childNodes).forEach(function (n) { frag.appendChild(n); });
      this.shelf.replaceChildren(frag);
      this.shelf.setAttribute('aria-busy', 'false');
      this.statusEl.textContent = '';
      var list = this.shelf.querySelector('[data-gbi-shelf-list]');
      if (list) list.scrollLeft = state.scroll[this.cur.url] || 0;
      this.arrows();
    }

    fail() {
      var self = this;
      this.shelf.setAttribute('aria-busy', 'false');
      this.shelf.innerHTML = '';
      var box = doc.createElement('div');
      box.className = 'gbi-cshop__error';
      var p = doc.createElement('p');
      p.style.margin = '0';
      p.textContent = this.s.error || '';
      var actions = doc.createElement('div');
      actions.className = 'gbi-cshop__error-actions';
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'gbi-cshop__retry';
      btn.textContent = this.s.retry || 'Try again';
      btn.addEventListener('click', function () { self.show(); });
      actions.appendChild(btn);
      box.appendChild(p);
      box.appendChild(actions);
      this.shelf.appendChild(box);
      this.statusEl.textContent = '';
      this.arrows();
    }

    // ---------------------------------------------------------------- events
    onClick(e) {
      var t = e.target;
      var tab = t.closest('[role="tab"]');
      if (tab && this.rail.contains(tab)) {
        if (tab.getAttribute('aria-selected') !== 'true') {
          var from = this.cur ? this.cur.label : '';
          this.select(tab.dataset.key, null);
          this.reveal(tab);
          track('cart_tab_switch', { from: from, to: this.cur.label, pos: this.cur.pos, level: 'tab' });
        }
        return;
      }
      var sub = t.closest('.gbi-cshop__sub');
      if (sub) {
        if (sub.getAttribute('aria-pressed') !== 'true') {
          var from2 = this.cur ? this.cur.label : '';
          this.select(this.st.key, sub.dataset.key);
          track('cart_tab_switch', { from: from2, to: this.cur.label, pos: this.cur.pos, level: 'sub' });
        }
        return;
      }
      var arrow = t.closest('[data-gbi-scroll]');
      if (arrow) {
        var sc = arrow.getAttribute('data-gbi-scroll') === 'rail' ? this.rail : this.shelf.querySelector('[data-gbi-shelf-list]');
        if (sc) sc.scrollBy({ left: Number(arrow.dataset.dir) * Math.round(sc.clientWidth * 0.8), behavior: reduced() ? 'auto' : 'smooth' });
        return;
      }
      var add = t.closest('[data-gbi-add]');
      if (add) {
        e.preventDefault();
        var card = add.closest('.gbi-ccard');
        if (add.hasAttribute('data-gbi-multi')) this.openSheet(card, add);
        else this.add(add.dataset.variantId, add, card, false);
      }
    }

    onKey(e) {
      var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      var i = this.tabs.indexOf(doc.activeElement);
      if (i === -1) return;
      e.preventDefault();
      var n = this.tabs.length;
      var next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
      this.tabs[i].tabIndex = -1;
      this.tabs[next].tabIndex = 0;
      this.tabs[next].focus({ preventScroll: true });
      this.reveal(this.tabs[next]);
    }

    onScroll(e) {
      var self = this;
      var list = e.target && e.target.matches && e.target.matches('[data-gbi-shelf-list]') ? e.target : null;
      if (list && this.cur) state.scroll[this.cur.url] = list.scrollLeft;
      if (this._raf) return;
      this._raf = requestAnimationFrame(function () { self._raf = 0; self.arrows(); });
    }

    arrows() {
      var self = this;
      this.querySelectorAll('[data-gbi-scroll]').forEach(function (a) {
        var sc = a.getAttribute('data-gbi-scroll') === 'rail' ? self.rail : self.shelf.querySelector('[data-gbi-shelf-list]:not(.gbi-cshelf__list--skeleton)');
        var dir = Number(a.dataset.dir);
        var can = !!sc && (dir < 0 ? sc.scrollLeft > 4 : sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 4);
        a.hidden = !can;
      });
    }

    // ---------------------------------------------------------------- quick add
    add(variantId, btn, card, fromSheet) {
      var self = this;
      // One add at a time: two taps on two cards within the ~550 ms of an add would run two /cart/add.js +
      // renderContents whose answers can land out of order (the older drawer HTML rendered last).
      if ((adding && Date.now() - adding < 10000) || !btn || btn.getAttribute('aria-disabled') === 'true' || !variantId) return;
      if (!window.GBI || !GBI.cart || typeof GBI.cart.add !== 'function') { window.location.href = card.querySelector('a').href; return; }
      adding = Date.now();
      var drawer = this.drawer;
      var opener = (drawer && drawer.activeElement && drawer.activeElement.isConnected && !drawer.contains(drawer.activeElement)) ? drawer.activeElement : doc.querySelector('#cart-icon-bubble');
      var msg = card.querySelector('.gbi-ccard__msg');
      if (msg) msg.textContent = '';
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-busy');
      this.statusEl.textContent = this.s.loading || '';
      var info = { handle: card.getAttribute('data-handle'), variant_id: Number(variantId), tab: this.cur ? this.cur.label : '', pos: Number(card.getAttribute('data-pos')) || 0, multi: !!fromSheet, place: this.place };
      var addedText = this.s.added || '';
      GBI.cart.add([{ id: Number(variantId), quantity: 1 }], { source: 'cart-shelf', opener: opener }).then(function () {
        adding = 0;
        if (info.place === 'more') state.more = false;   // the 2nd kit is in: the row is gone, keep it closed
        track('cart_quick_add', info);
        var live = drawer && drawer.querySelector('#CartDrawer-LiveRegionText');
        if (live && addedText) setTimeout(function () { live.textContent = addedText; }, 150);
        // The drawer re-rendered into the filled state (this element is gone). Re-trap focus in the new
        // content, like the theme's open() would after a transition (the drawer was already open).
        if (drawer) {
          var root = drawer.querySelector('#CartDrawer');
          var inner = drawer.querySelector('.drawer__inner');
          try {
            if (typeof window.trapFocus === 'function' && root && inner) window.trapFocus(root, inner);
            else if (inner) inner.focus({ preventScroll: true });
          } catch (e) { if (inner) inner.focus(); }
        }
      }).catch(function (err) {
        adding = 0;
        if (!btn.isConnected) return;
        btn.classList.remove('is-busy');
        self.statusEl.textContent = '';
        var soldOut = /sold out|0 of|not available|unavailable/i.test(String(err && err.message));
        if (soldOut) {
          btn.setAttribute('aria-disabled', 'true');
          btn.removeAttribute('aria-haspopup');
          // Keep the visually hidden " – <title>" so the button's name stays "Sold out – <title>".
          var lab = btn.querySelector('.gbi-ccard__add-label');
          if (lab) lab.textContent = self.s.sold_out || ''; else btn.textContent = self.s.sold_out || '';
          var ic = btn.querySelector('svg');
          if (ic) ic.remove();
        } else {
          btn.removeAttribute('aria-disabled');
        }
        if (!msg) {
          msg = doc.createElement('p');
          msg.className = 'gbi-ccard__msg';
          msg.setAttribute('role', 'status');
          card.appendChild(msg);
        }
        msg.textContent = soldOut ? '' : (self.s.add_error || '');
        if (soldOut) btn.focus();
      });
    }

    // ---------------------------------------------------------------- version sheet
    openSheet(card, btn) {
      var sheet = this.sheet;
      var data;
      try { data = JSON.parse(card.querySelector('[data-gbi-variants]').textContent); } catch (e) { data = null; }
      if (!sheet || !data || typeof sheet.showModal !== 'function') { window.location.href = card.querySelector('a').href; return; }
      this.sheetCard = card;
      this.sheetBtn = btn;
      var img = card.querySelector('.gbi-ccard__img');
      var simg = sheet.querySelector('[data-gbi-csheet-img]');
      if (img && simg) { simg.src = img.currentSrc || img.src; }
      sheet.querySelector('[data-gbi-csheet-title]').textContent = card.querySelector('.gbi-ccard__title').textContent.trim();
      sheet.querySelector('[data-gbi-csheet-facts]').textContent = Array.prototype.map.call(card.querySelectorAll('.gbi-ccard__fact'), function (f) { return f.textContent.trim(); }).join(' · ');
      sheet.querySelector('[data-gbi-csheet-legend]').textContent = data.options || '';
      sheet.querySelector('[data-gbi-csheet-error]').textContent = '';
      var details = sheet.querySelector('[data-gbi-csheet-details]');
      details.href = card.querySelector('a').href;
      this.fill(details);
      var list = sheet.querySelector('[data-gbi-csheet-list]');
      list.textContent = '';
      var first = null;
      var s = this.s;
      var name = 'gbi-csheet-' + card.getAttribute('data-handle');
      data.variants.forEach(function (v, i) {
        var label = doc.createElement('label');
        label.className = 'gbi-csheet__opt';
        var input = doc.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = String(v.id);
        input.dataset.price = v.price;
        if (!v.available) input.disabled = true;
        if (v.available && !first) { first = input; input.checked = true; }
        var dot = doc.createElement('span');
        dot.className = 'gbi-csheet__dot';
        dot.setAttribute('aria-hidden', 'true');
        var nm = doc.createElement('span');
        nm.className = 'gbi-csheet__opt-name';
        nm.textContent = v.title;
        var pr = doc.createElement('span');
        pr.className = 'gbi-csheet__opt-price';
        pr.textContent = v.available ? v.price : (s.sold_out || '');
        label.appendChild(input);
        label.appendChild(dot);
        label.appendChild(nm);
        label.appendChild(pr);
        list.appendChild(label);
      });
      this.sheetPrice();
      var inner = this.drawer && this.drawer.querySelector('.drawer__inner');
      if (inner) sheet.style.setProperty('--gbi-csheet-w', Math.round(inner.getBoundingClientRect().width) + 'px');
      sheet.showModal();
      if (first) first.focus();
    }

    sheetPrice() {
      var sheet = this.sheet;
      var checked = sheet.querySelector('input[type="radio"]:checked');
      var label = sheet.querySelector('[data-gbi-csheet-add-label]');
      var addBtn = sheet.querySelector('[data-gbi-csheet-add]');
      label.textContent = '';
      label.appendChild(doc.createTextNode((this.s.sheet_add || '') + (checked ? ' · ' : '')));
      if (checked) {
        var m = doc.createElement('span');
        m.className = 'gbi-mono';
        m.textContent = checked.dataset.price;
        label.appendChild(m);
      }
      if (checked) addBtn.removeAttribute('aria-disabled'); else addBtn.setAttribute('aria-disabled', 'true');
    }

    onSheetChange(e) { if (e.target && e.target.type === 'radio') this.sheetPrice(); }

    onSheetClick(e) {
      var t = e.target;
      // Backdrop tap only: the <dialog> is also the target of a tap in its own padding, so close only when the
      // point is outside the dialog box (a keyboard click has no coordinates: never closes here).
      if (t === this.sheet) {
        var r = this.sheet.getBoundingClientRect();
        if (e.detail > 0 && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) this.sheet.close();
        return;
      }
      if (t.closest('[data-gbi-csheet-close]')) { this.sheet.close(); return; }
      if (t.closest('[data-gbi-csheet-add]')) {
        var checked = this.sheet.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        var card = this.sheetCard;
        var btn = this.sheetBtn;
        this.sheet.close();
        this.add(checked.value, btn, card, true);
      }
    }

    onSheetClose() {
      if (this.sheetBtn && this.sheetBtn.isConnected && doc.activeElement !== this.sheetBtn) {
        try { this.sheetBtn.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }
  }
  customElements.define('gbi-cart-shop', GbiCartShop);

  // ------------------------------------------------------------------ "Add another kit" (spec §4.7)
  // A collapsed row under the line items while the cart holds exactly ONE kit (snippets/gbi-cart-more.liquid,
  // section setting shop_more_kits). Opening it inserts a copy of the empty cart's shop (same rail, chips,
  // cache and quick add), so the tab and chip the shopper was browsing come back from the cache with no
  // request. With no copy available (a visit that never showed an empty cart), the row stays hidden.
  function scroller(el) {
    for (var n = el && el.parentElement; n && n !== doc.body; n = n.parentElement) {
      var oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
    }
    return null;
  }
  class GbiCartMore extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var tpl = shopTpl || ss(TPL_KEY);
      this.btn = this.querySelector('[data-gbi-cmore-toggle]');
      this.panel = this.querySelector('[data-gbi-cmore-panel]');
      if (!tpl || tpl.indexOf('<gbi-cart-shop') !== 0 || !this.btn || !this.panel) return;
      this.tpl = tpl;
      this.hidden = false;
      this.onToggle = this.toggle.bind(this);
      this.btn.addEventListener('click', this.onToggle);
      if (state.more) this.expand(false);   // a re-render (BOGOS, timer refresh) keeps it open
    }
    disconnectedCallback() {
      if (this.btn && this.onToggle) this.btn.removeEventListener('click', this.onToggle);
      this._ready = false;
    }
    expand(byUser) {
      var self = this;
      if (!this.panel.firstElementChild) this.panel.innerHTML = this.tpl;
      this.panel.hidden = false;
      this.btn.setAttribute('aria-expanded', 'true');
      state.more = true;
      if (!byUser) return;
      track('cart_more_open', { kits: 1 });
      // Scroll the drawer body so the rail sits at the top: rail, shelf head and the first cards WITH their add
      // buttons fit at 375 × 812 (the toggle is one short scroll up).
      requestAnimationFrame(function () {
        var sc = scroller(self);
        if (!sc) return;
        var anchor = self.panel.querySelector('.gbi-cshop__railwrap') || self;
        var top = sc.scrollTop + anchor.getBoundingClientRect().top - sc.getBoundingClientRect().top - 4;
        sc.scrollTo({ top: Math.max(0, top), behavior: reduced() ? 'auto' : 'smooth' });
      });
    }
    collapse() {
      this.panel.hidden = true;
      this.btn.setAttribute('aria-expanded', 'false');
      state.more = false;
    }
    toggle() {
      if (this.btn.getAttribute('aria-expanded') === 'true') this.collapse(); else this.expand(true);
    }
  }
  customElements.define('gbi-cart-more', GbiCartMore);

  // ------------------------------------------------------------------ "Protect your <kit>" (2026-10-14)
  // One-tap add for the display case row (snippets/gbi-cart-case.liquid). The drawer re-renders without the row
  // (a case is now in the cart); focus is trapped again in the new drawer content, like a shelf quick add.
  doc.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-gbi-ccase-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true' || (adding && Date.now() - adding < 10000)) return;
    if (!window.GBI || !GBI.cart || typeof GBI.cart.add !== 'function') return;
    var id = Number(btn.getAttribute('data-variant-id'));
    if (!id) return;
    adding = Date.now();
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('is-busy');
    var drawer = btn.closest('cart-drawer');
    GBI.cart.add([{ id: id, quantity: 1 }], { source: 'cart-case', opener: doc.querySelector('#cart-icon-bubble') }).then(function () {
      adding = 0;
      track('cart_case_add', { variant_id: id });
      if (drawer) {
        var root = drawer.querySelector('#CartDrawer');
        var inner = drawer.querySelector('.drawer__inner');
        try {
          if (typeof window.trapFocus === 'function' && root && inner) window.trapFocus(root, inner);
          else if (inner) inner.focus({ preventScroll: true });
        } catch (err) { if (inner) inner.focus(); }
      }
    }).catch(function () {
      adding = 0;
      if (!btn.isConnected) return;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-busy');
    });
  });

  // ------------------------------------------------------------------ reward celebration (spec §5.4)
  var SEEN = 'gbi:reward-seen';
  var KITS = 'gbi:reward-kits';
  function store(k, v) {
    try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch (e) { return null; }
    return null;
  }
  function celebrate() {
    var el = doc.querySelector('cart-drawer .gbi-reward[data-gbi-reward="filled"]');
    if (!el) { if (doc.querySelector('cart-drawer.is-empty')) store(KITS, '0'); return; }
    if (el.__gbiSeen) return;
    el.__gbiSeen = true;
    var kits = Number(el.getAttribute('data-gbi-kits')) || 0;
    var last = Number(store(KITS)) || 0;
    if (!store(SEEN)) { el.classList.add('is-new'); store(SEEN, '1'); }
    else if (kits > last && last > 0) el.classList.add('is-bump');
    store(KITS, String(kits));
  }
  function watchDrawer() {
    var drawer = doc.querySelector('cart-drawer');
    if (!drawer) return;
    var pending = 0;
    // The reserve timer rewrites its text every second: those mutations are skipped, so the observer does
    // not wake the page every second for the whole visit.
    var inTimer = function (r) {
      var t = r.target && (r.target.nodeType === 1 ? r.target : r.target.parentElement);
      return !!(t && t.closest && t.closest('.cart-timer, countdown-timer'));
    };
    new MutationObserver(function (records) {
      if (pending || records.every(inTimer)) return;
      pending = requestAnimationFrame(function () {
        pending = 0;
        // "Add another kit" stays open only across re-renders of the same 1-kit cart: once a drawer renders
        // without the row (2+ kits however they were added, or empty), the next 1-kit cart starts closed.
        if (!drawer.querySelector('gbi-cart-more')) state.more = false;
        celebrate();
      });
    }).observe(drawer, { childList: true, subtree: true });
    var el = doc.querySelector('cart-drawer .gbi-reward[data-gbi-reward="filled"]');
    if (el) { el.__gbiSeen = true; store(KITS, el.getAttribute('data-gbi-kits') || '0'); if (!store(SEEN)) store(SEEN, '1'); }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();
})();
